import { useCallback, useEffect } from 'react';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import { isEmptyResponse } from '../utils/checkEmptyResponse';
import { formatComponentNumber, googleDataLayerPush } from '../utils/analytics';
import useSitecoreContext from './useSitecoreContext';

const exceptComponentNames = [
  'BreadcrumbBar',
  'GenericPopUp',
  'NavigationBarAnchorComponent',
  'ProductBreadcrumbBar',
  'ProductSeoTags',
  'ProductSeriesNavigationBar',
  'ProductSeriesDetailNavigationOverview',
  'QuickLinks',
  'ProductModelNavigation',
];

const useAnalyticsActionOnScroll = (
  rendering: ComponentRendering,
  refElement: React.MutableRefObject<HTMLDivElement | null>,
  title: string,
  template: string
): void => {
  let isActionExecuted = false;
  const {sitecoreContext} = useSitecoreContext();

  const isEditorialEmptyResponse = (component: any, excludeComponentNames: string[]) => {
    const fields = component?.fields;
    if (Object.keys(fields).length > 0 && !excludeComponentNames.includes(component?.componentName)) {
      return (
        component?.fields?.items?.length > 0 ||
        component?.fields?.apiData?.items?.length > 0 ||
        component?.fields?.innerComponents?.length > 0
      );
    }
    return Object.keys(fields).length > 0;
  };

  const isProductEmptyResponse = (component: any, excludeComponentNames: string[]): boolean => {
    if (isEmptyResponse(component) && !excludeComponentNames.includes(component?.componentName)) {
      const apiDataLength =
        component?.fields?.apiData?.items?.length > 0 ||
        component?.fields?.apiData?.innerComponents?.length > 0 ||
        component?.fields?.apiData?.models?.length > 0 ||
        component?.fields?.apiData?.brochures?.length > 0 ||
        component?.fields?.apiData?.lines?.items?.length > 0 ||
        component?.fields?.apiData?.series?.items?.length > 0;
      const isChildrenPopulated = component?.componentName?.toLowerCase()?.includes('compatible')
        ? component?.fields?.apiData?.items?.some((e) => e?.items?.length > 0)
        : true;
      return apiDataLength && isChildrenPopulated;
    }
    return isEmptyResponse(component);
  };

  const isEmptyResponseValue = (component: any): boolean => {
    //components which doesn't have items array
    const excludeComponentNames = [
      'EventsListing', // has items array but will be visible even if items are empty
      'EventDetail',
      'LiveEventDetail',
      'NewsListing', // has items array but will be visible even if items are empty
      'NewsDetail',
      'ProductHeroBanner',
      'ProductHeroBannerSeriesDetail',
      'ProductModelDetailHeroSection',
      'ProductSeriesDetailTestimonial',
      'ProductSeriesOverview',
      'SmallHero',
      'SplitModule',
      'TextModule',
      'Footer',
    ];
    if (component?.componentName.startsWith('Product')) {
      return isProductEmptyResponse(component, excludeComponentNames);
    } else {
      return isEditorialEmptyResponse(component, excludeComponentNames);
    }
  };

  const isComponentsValid = (component: any) => !!component.uid && isEmptyResponseValue(component);
  const isComponentInTheList = (component: any, componentList: Array<string>) => componentList.indexOf(component.componentName) >= 0;

  const getComponentsFromGenericPopUp = (component: any) => {
    if (isComponentInTheList(component, ['GenericPopUp'])) {
      return component.placeholders?.['jss-popup'];
    }

    if (component?.placeholders?.['jss-generic-popup']?.length <= 0) {
      return [];
    }

    return (
      component?.placeholders?.['jss-generic-popup']
        ?.map((genericPopUpComponent) => genericPopUpComponent.placeholders?.['jss-popup'])
        .filter((newcomponent) => newcomponent)
        .flat(1) ?? []
    );
  };

  const isInOverlay = (newSitecoreContext) => {
    if (!rendering) {
      return true;
    }

    const componentList = newSitecoreContext?.route.placeholders['jss-main'];

    const genericPopUpsComponentIds = componentList
      .map((component) => getComponentsFromGenericPopUp(component))
      .flat(1)
      .map((component) => component.uid);

    const genericPopUpsComponentsIndex = genericPopUpsComponentIds.indexOf(rendering.uid);

    return genericPopUpsComponentsIndex >= 0;
  };

  const getComponentNumber = (sitecoreContext, inOverlayValue) => {
    if (inOverlayValue) {
      return '00-overlay';
    }
    const jssMain = sitecoreContext?.route?.placeholders['jss-main'];
    const jssFooter = sitecoreContext?.route.placeholders['jss-footer'];
    const componentList = [...jssMain, ...jssFooter];

    const validComponentIds = componentList
      .filter((component) => isComponentsValid(component))
      .filter((component) => !isComponentInTheList(component, exceptComponentNames))
      .map((component) => component.uid);

    const componentIndex = validComponentIds.indexOf(rendering.uid);

    return formatComponentNumber(componentIndex + 1);
  };

  const inOverlay = isInOverlay(sitecoreContext);
  const elementNumber = getComponentNumber(sitecoreContext, inOverlay);

  const callAnalyticsAction = () => {
    if (isActionExecuted) {
      return;
    }

    isActionExecuted = true;

    const dataLayerTemplate = inOverlay ? `Overlay ${template}` : template;

    const dataLayerData = {
      event: 'visible',
      _clear: true,
      'data-tag-meta': {
        type: 'component',
        title: title ?? dataLayerTemplate,
        template: dataLayerTemplate,
        componentNo: elementNumber,
      },
    };

    googleDataLayerPush(dataLayerData);
  };

  const handleIfIsIntersectedElement = (element) => {
    if (!element) return;

    if (element.isIntersecting) {
      callAnalyticsAction();
    }
  };

  const handleIntersector = (entries) => {
    entries.forEach((element) => handleIfIsIntersectedElement(element));
  };

  const analyticsActionCallback = useCallback(handleIntersector, []);

  useEffect(() => {
    if (!refElement?.current) return;
    const observer = new IntersectionObserver(analyticsActionCallback, { threshold: 0.01 });
    observer.observe(refElement?.current);

    return (): void => observer.disconnect();
  }, [analyticsActionCallback, refElement]);
};

export default useAnalyticsActionOnScroll;
